import React, { Fragment, useMemo } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AllMainTreatmentTr from "./AllMainTreatmentTr";
import CaseTreatmentSection from "./CaseTreatmentSection";
import TreatmentFormModal from "../treatmentform/TreatmentFormModal";

const DEFAULT_MAIN_TREATMENT_STATUS = "Complete";

const toDateKey = (value?: string) => {
  if (!value) return "Unknown";
  const key = String(value).split("T")[0];
  return key || "Unknown";
};

const getCategory = (item: any) => {
  const name = String(item?.treatment ?? "").trim();
  return name || "Other";
};

interface DateWiseGroup {
  date: string;
  categories: { category: string; items: any[] }[];
}

interface DateWiseTreatmentsProps {
  allTeatment: any[];
  setallTeatment: (value: any) => void;
  fetchData: () => void;
  refresh: () => void;
  editingTreatmentId: string | null;
  editingTreatment: any;
  closeEditForm: () => void;
  handleEditTreatment: (item: any) => void;
  removediagnose: (item: any) => void;
  openSubTreatmentId: string | null;
  setOpenSubTreatmentId: React.Dispatch<React.SetStateAction<string | null>>;
  setEditingTreatmentId: React.Dispatch<React.SetStateAction<string | null>>;
}

function DateWiseTreatments({
  allTeatment,
  setallTeatment,
  fetchData,
  refresh,
  editingTreatmentId,
  editingTreatment,
  closeEditForm,
  handleEditTreatment,
  removediagnose,
  openSubTreatmentId,
  setOpenSubTreatmentId,
  setEditingTreatmentId,
}: DateWiseTreatmentsProps) {
  const dateWiseGroups = useMemo<DateWiseGroup[]>(() => {
    const byDate = new Map<string, any[]>();

    (allTeatment ?? []).forEach((item: any) => {
      const date = toDateKey(item?.createdAt);
      const list = byDate.get(date) ?? [];
      list.push(item);
      byDate.set(date, list);
    });

    return Array.from(byDate.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, items]) => {
        const byCategory = new Map<string, any[]>();
        items.forEach((item) => {
          const category = getCategory(item);
          const list = byCategory.get(category) ?? [];
          list.push(item);
          byCategory.set(category, list);
        });
        const categories = Array.from(byCategory.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([category, categoryItems]) => ({
            category,
            items: categoryItems,
          }));
        return { date, categories };
      });
  }, [allTeatment]);

  if (dateWiseGroups.length === 0) {
    return (
      <div className="py-10 text-center text-slate-400 dark:text-slate-500">
        <CalendarMonthIcon sx={{ fontSize: 36 }} className="mb-2" />
        <p className="text-xs">No treatments to show by date</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-700 -mx-3 -mb-3">
      {dateWiseGroups.map((group) => (
        <div key={group.date} className="p-3 space-y-2.5">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold">
            <CalendarMonthIcon sx={{ fontSize: 14 }} />
            {group.date}
          </div>

          {group.categories.map(({ category, items }) => (
            <div
              key={`${group.date}-${category}`}
              className="rounded-lg border border-amber-200 dark:border-amber-800 overflow-hidden"
            >
              <div className="px-3 py-1.5 bg-amber-100/80 dark:bg-amber-900/40 border-b border-amber-200 dark:border-amber-800">
                <span className="text-xs font-semibold text-amber-900 dark:text-amber-300 uppercase tracking-wide">
                  {category}
                </span>
                <span className="ml-2 text-[10px] text-amber-700/80 dark:text-amber-400/80">
                  {items.length} {items.length === 1 ? "treatment" : "treatments"}
                </span>
              </div>
              <div className="overflow-x-auto bg-[#fffbeb]/50 dark:bg-amber-900/10">
                <table className="w-full">
                  <thead className="bg-amber-50/80 dark:bg-amber-900/30">
                    <tr>
                      {[
                        "Date",
                        "Treatment",
                        "Tooth No",
                        "Qty",
                        "Price",
                        "Status",
                        "Dentist",
                        "Note",
                        "Action",
                      ].map((header) => (
                        <th
                          key={header}
                          className="px-3 py-1.5 text-left text-[10px] font-semibold text-amber-800 dark:text-amber-400 uppercase"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100 dark:divide-amber-800/50">
                    {items.map((item: any, index: number) => (
                      <Fragment key={item._id ?? `${group.date}-${category}-${index}`}>
                        {String(editingTreatmentId) === String(item._id) &&
                        editingTreatment ? (
                          <TreatmentFormModal
                            key={String(item._id)}
                            inlineRow
                            defaultStatus={DEFAULT_MAIN_TREATMENT_STATUS}
                            treatment={allTeatment}
                            setaddTreatmentOpent={closeEditForm}
                            settreatment={setallTeatment}
                            fetchData={fetchData}
                            editItem={editingTreatment}
                            onClose={closeEditForm}
                          />
                        ) : (
                          <AllMainTreatmentTr
                            item={item}
                            handleEditTreatment={handleEditTreatment}
                            onClick={() => removediagnose(item)}
                            refresh={refresh}
                            onAddSubTreatment={() => {
                              setEditingTreatmentId(null);
                              setOpenSubTreatmentId((prev) =>
                                prev === item._id ? null : item._id
                              );
                            }}
                            isSubFormOpen={openSubTreatmentId === item._id}
                          />
                        )}
                        <CaseTreatmentSection
                          item={item}
                          allTeatment={allTeatment}
                          setallTeatment={setallTeatment}
                          refresh={refresh}
                          showAddForm={openSubTreatmentId === item._id}
                          onCloseAddForm={() => setOpenSubTreatmentId(null)}
                        />
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default DateWiseTreatments;
